import React, { useEffect, useRef } from 'react';
import { cn } from '@cotera/client/v0/lib/utils';
import { Card } from '@cotera/client/v0/components/ui/card';
import type {
  SuggestionItem,
  SuggestionMenuRenderProps,
} from '@cotera/client/app/components/ui/tiptap/tiptap-ui-utils/suggestion-menu';
import type { UpstreamFieldOption } from '../variable-picker-data';
import {
  VariablePickerRow,
  VARIABLE_PICKER_EMPTY_TEXT,
  VARIABLE_PICKER_WIDTH,
} from '../variable-picker';

/** Extra per-row data carried on each suggestion item's `context`. */
export type VariableItemContext = {
  readonly field: UpstreamFieldOption;
};

type Props = SuggestionMenuRenderProps<VariableItemContext>;

const Row: React.FC<{
  item: SuggestionItem<VariableItemContext>;
  selected: boolean;
  onSelect: () => void;
}> = ({ item, selected, onSelect }) => {
  const rowRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (selected) {
      rowRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [selected]);

  const field = item.context?.field;
  return (
    <button
      ref={rowRef}
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm',
        selected ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/60'
      )}
    >
      {field === undefined ? item.title : <VariablePickerRow field={field} />}
    </button>
  );
};

/**
 * The card body rendered inside the `/` variable suggestion menu.
 *
 * The menu surface of the shared picker: the rows, the empty state and the
 * keyboard legend all come from `variable-picker`, so this and the bolt popover
 * differ only in what drives the selection — the editor's suggestion plugin
 * here, cmdk there.
 */
export const VariablePickerList: React.FC<Props> = ({
  items,
  selectedIndex,
  onSelect,
}) => {
  if (items.length === 0) {
    return (
      <Card
        className={cn(
          'text-muted-foreground p-3 text-sm',
          VARIABLE_PICKER_WIDTH
        )}
      >
        {VARIABLE_PICKER_EMPTY_TEXT}
      </Card>
    );
  }

  const rows: React.ReactNode[] = [];
  let lastGroup: string | undefined;
  items.forEach((item, index) => {
    if (item.group !== undefined && item.group !== lastGroup) {
      lastGroup = item.group;
      rows.push(
        // Frozen to the top of the scroll area so the source stays visible
        // while scrolling its run of fields — the heading carries the
        // attribution that used to sit on every row as a chip. Filled band and
        // stronger text so the frozen header reads as a divider, not as faint
        // gray that blends into the rows beneath it.
        <div
          key={`group-${item.group}`}
          className="bg-muted text-foreground/80 sticky top-0 z-10 border-b border-foreground/10 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide"
        >
          {item.group}
        </div>
      );
    }
    rows.push(
      <Row
        key={`row-${index}`}
        item={item}
        selected={index === selectedIndex}
        onSelect={() => onSelect(item)}
      />
    );
  });

  return (
    <Card
      className={cn('flex flex-col overflow-hidden p-0', VARIABLE_PICKER_WIDTH)}
      // Cap to whatever room the floating menu actually has (set by the
      // suggestion menu's `size` middleware), never taller than 320px. Without
      // this the list reserved a flat 320px and, now that it portals out of the
      // inspector's clipping, ran off the bottom of the window near a low input.
      style={{
        maxHeight: 'min(320px, var(--suggestion-menu-max-height, 320px))',
      }}
    >
      {/* No top padding: the sticky source heading anchors at `top-0`, so any
          padding above it is a strip the scrolling rows show through. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-1 pt-0">{rows}</div>
    </Card>
  );
};
